'use client'

import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { useUser } from '@stackframe/stack'
import { useState, useEffect } from 'react'
import {
  BsHouseDoor,
  BsTrophy,
  BsTicketPerforated,
  BsCupStraw,
  BsCameraReels,
  BsBalloon,
  BsBuilding,
  BsStar,
  BsBullseye,
  BsBarChart,
  BsPeople,
  BsLightning,
  BsBox,
  BsFileText,
  BsArrowRepeat,
  BsPerson,
  BsBoxArrowRight,
  BsImage,
  BsMegaphone,
  BsTree,
} from 'react-icons/bs'
import { ReactNode } from 'react'

// Best Of subcategories
const bestOfCategories: { href: string; label: string; icon: ReactNode }[] = [
  { href: '/best', label: 'Find Best Of...', icon: <BsTrophy /> },
  { href: '/best/attractions', label: 'Attractions', icon: <BsTicketPerforated /> },
  { href: '/best/restaurants', label: 'Restaurants', icon: <BsCupStraw /> },
  { href: '/best/shows', label: 'Shows', icon: <BsCameraReels /> },
  { href: '/best/entertainment', label: 'Entertainment', icon: <BsBalloon /> },
  { href: '/best/resorts', label: 'Resorts', icon: <BsBuilding /> },
  { href: '/best/magic-kingdom', label: 'Magic Kingdom', icon: <BsTree /> },
  { href: '/best/epcot', label: 'EPCOT', icon: <BsTree /> },
  { href: '/best/hollywood-studios', label: 'Hollywood Studios', icon: <BsTree /> },
  { href: '/best/animal-kingdom', label: 'Animal Kingdom', icon: <BsTree /> },
]

// Admin subcategories
const adminCategories: { href: string; label: string; icon: ReactNode }[] = [
  { href: '/admin', label: 'Dashboard', icon: <BsBarChart /> },
  { href: '/admin/entities', label: 'Entities', icon: <BsBox /> },
  { href: '/admin/entities/missing-images', label: 'Missing Images', icon: <BsImage /> },
  { href: '/admin/blog', label: 'Blog Posts', icon: <BsFileText /> },
  { href: '/admin/challenges', label: 'Challenges', icon: <BsTrophy /> },
  { href: '/admin/users', label: 'Users', icon: <BsPeople /> },
  { href: '/admin/advertise', label: 'Advertise', icon: <BsMegaphone /> },
  { href: '/admin/sync', label: 'Sync Data', icon: <BsArrowRepeat /> },
  { href: '/admin/social', label: 'Social Images', icon: <BsImage /> },
]

export function SideNav() {
  const pathname = usePathname()
  const router = useRouter()
  const user = useUser()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isBestOfExpanded, setIsBestOfExpanded] = useState(pathname.startsWith('/best'))
  const [isAdminExpanded, setIsAdminExpanded] = useState(pathname.startsWith('/admin'))
  const [isAdmin, setIsAdmin] = useState(false)
  const [dbImage, setDbImage] = useState<string | null>(null)

  // Fetch user role and avatar from our database
  useEffect(() => {
    if (user) {
      fetch('/api/user/profile')
        .then(res => res.json())
        .then(data => {
          if (data.user?.role === 'ADMIN') {
            setIsAdmin(true)
          }
          setDbImage(data.user?.image ?? null)
        })
        .catch(() => {
          // Ignore errors
        })
    } else {
      setIsAdmin(false)
      setDbImage(null)
    }
  }, [user])

  const isActive = (path: string) => {
    if (path === '/') return pathname === '/'
    return pathname.startsWith(path)
  }

  const handleBestOfClick = () => {
    if (!isBestOfExpanded) {
      // Expand and navigate to the wizard
      setIsBestOfExpanded(true)
      router.push('/best')
    } else {
      // Toggle collapse
      setIsBestOfExpanded(!isBestOfExpanded)
    }
  }

  const handleAdminClick = () => {
    if (!isAdminExpanded) {
      // Expand and navigate to admin dashboard
      setIsAdminExpanded(true)
      router.push('/admin')
    } else {
      // Toggle collapse
      setIsAdminExpanded(!isAdminExpanded)
    }
  }

  // Authenticated-only nav links
  const authNavLinks: { href: string; label: string; icon: ReactNode }[] = [
    { href: '/challenges', label: 'Challenges', icon: <BsBullseye /> },
    { href: '/dashboard/user', label: 'My Dashboard', icon: <BsBarChart /> },
    { href: '/dashboard/community', label: 'Community', icon: <BsPeople /> },
  ]

  // Authenticated-only nav links
  const unAuthNavLinks: { href: string; label: string; icon: ReactNode }[] = [
    { href: '/rate', label: 'Rate It', icon: <BsStar /> },
    { href: '/blog', label: 'Blog', icon: <BsFileText /> },
  ]

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-[#193f8b] border-r border-slate-700 fixed left-0 top-0 bottom-0 z-40">
        {/* Logo */}
        <div className="p-4 border-b border-slate-700 shrink-0">
          <Link href="/" className="block">
            <img
              src="/logo.png"
              alt="RateWDW Logo"
              className="w-full h-auto max-h-24 object-contain"
            />
          </Link>
        </div>

        {/* Navigation Links — only this region scrolls when menus expand */}
        <nav className="flex-1 min-h-0 overflow-y-auto p-4 sidebar-scroll">
          <ul className="space-y-1">
            {/* Home link */}
            <li>
              <Link
                href="/"
                className={`flex items-center gap-3 px-4 py-3 font-medium transition-colors rounded-lg ${
                  pathname === '/'
                    ? 'bg-slate-800 text-[#FEDB1A]'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-[#FEDB1A]'
                }`}
              >
                <BsHouseDoor />
                <span>Home</span>
              </Link>
            </li>

            {/* Best Of - Expandable */}
            <li>
              <button
                onClick={handleBestOfClick}
                className={`w-full flex items-center gap-3 px-4 py-3 font-medium transition-colors rounded-lg ${
                  pathname.startsWith('/best')
                    ? 'bg-slate-800 text-[#FEDB1A]'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-[#FEDB1A]'
                }`}
              >
                <BsTrophy />
                <span className="flex-1 text-left">Best Of</span>
                <svg
                  className={`w-4 h-4 transition-transform ${isBestOfExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isBestOfExpanded && (
                <ul className="ml-4 mt-1 space-y-1">
                  {bestOfCategories.map((cat) => (
                    <li key={cat.href}>
                      <Link
                        href={cat.href}
                        className={`flex items-center gap-3 px-4 py-2 text-sm font-medium transition-colors rounded-lg ${
                          pathname === cat.href
                            ? 'bg-slate-700 text-[#FEDB1A]'
                            : 'text-slate-400 hover:bg-slate-700 hover:text-[#FEDB1A]'
                        }`}
                      >
                        <span>{cat.icon}</span>
                        <span>{cat.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>

            {/* Un-authenticated nav links */}
            {unAuthNavLinks.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  className={`flex items-center gap-3 px-4 py-3 font-medium transition-colors rounded-lg ${
                    isActive(link.href)
                      ? 'bg-slate-800 text-[#FEDB1A]'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-[#FEDB1A]'
                  }`}
                >
                  <span>{link.icon}</span>
                  <span>{link.label}</span>
                </Link>
              </li>
            ))}


            {/* Authenticated nav links */}
            {user && authNavLinks.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  className={`flex items-center gap-3 px-4 py-3 font-medium transition-colors rounded-lg ${
                    isActive(link.href)
                      ? 'bg-slate-800 text-[#FEDB1A]'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-[#FEDB1A]'
                  }`}
                >
                  <span>{link.icon}</span>
                  <span>{link.label}</span>
                </Link>
              </li>
            ))}

            {/* Admin - Expandable */}
            {user && isAdmin && (
              <li className="pt-4">
                <div className="h-px bg-slate-600 mb-4" />
                <button
                  onClick={handleAdminClick}
                  className={`w-full flex items-center gap-3 px-4 py-3 font-medium transition-colors rounded-lg ${
                    pathname.startsWith('/admin')
                      ? 'bg-slate-800 text-[#55B1F1]'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-[#55B1F1]'
                  }`}
                >
                  <BsLightning />
                  <span className="flex-1 text-left">Admin</span>
                  <svg
                    className={`w-4 h-4 transition-transform ${isAdminExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isAdminExpanded && (
                  <ul className="ml-4 mt-1 space-y-1">
                    {adminCategories.map((cat) => (
                      <li key={cat.href}>
                        <Link
                          href={cat.href}
                          className={`flex items-center gap-3 px-4 py-2 text-sm font-medium transition-colors rounded-lg ${
                            pathname === cat.href
                              ? 'bg-slate-700 text-[#55B1F1]'
                              : 'text-slate-400 hover:bg-slate-700 hover:text-[#55B1F1]'
                          }`}
                        >
                          <span>{cat.icon}</span>
                          <span>{cat.label}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            )}
          </ul>
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-slate-700 shrink-0">
          {user ? (
            <div className="space-y-2">
              <Link
                href="/profile"
                className="flex items-center gap-3 px-4 py-2 hover:bg-slate-800 transition-colors rounded-lg group"
              >
                {dbImage ? (
                  <img
                    src={dbImage}
                    alt="Profile"
                    className="w-8 h-8 rounded-full border-2 border-transparent group-hover:border-[#FEDB1A] transition-colors"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                    <BsPerson />
                  </div>
                )}
                <span className="text-sm text-slate-300 group-hover:text-white break-words">
                  {user.displayName || user.primaryEmail}
                </span>
              </Link>
              <Link
                href="/auth/signout"
                className="block px-4 py-2 text-sm text-slate-400 hover:bg-slate-800 hover:text-white transition-colors rounded-lg"
              >
                Sign Out
              </Link>
            </div>
          ) : (
            <Link
              href="/auth/signin"
              className="block px-4 py-3 bg-[#1E4289] text-white hover:bg-[#FEDB1A] hover:text-slate-900 font-medium transition-colors text-center rounded-lg"
            >
              Sign In
            </Link>
          )}
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden bg-[#193f8b] border-b border-slate-700 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-2">
          <Link href="/" className="flex items-center">
            <img
              src="/logo.png"
              alt="RateWDW Logo"
              className="h-12 w-auto"
            />
          </Link>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-lg text-slate-300 hover:bg-slate-800"
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="border-t border-slate-700 py-4 px-4 space-y-1">
            {/* Home */}
            <Link
              href="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 font-medium transition-colors rounded-lg ${
                pathname === '/'
                  ? 'bg-slate-800 text-[#FEDB1A]'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-[#FEDB1A]'
              }`}
            >
              <BsHouseDoor />
              <span>Home</span>
            </Link>

            {/* Best Of - Expandable */}
            <div>
              <button
                onClick={handleBestOfClick}
                className={`w-full flex items-center gap-3 px-4 py-3 font-medium transition-colors rounded-lg ${
                  pathname.startsWith('/best')
                    ? 'bg-slate-800 text-[#FEDB1A]'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-[#FEDB1A]'
                }`}
              >
                <BsTrophy />
                <span className="flex-1 text-left">Best Of</span>
                <svg
                  className={`w-4 h-4 transition-transform ${isBestOfExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isBestOfExpanded && (
                <div className="ml-4 mt-1 space-y-1">
                  {bestOfCategories.map((cat) => (
                    <Link
                      key={cat.href}
                      href={cat.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-2 text-sm font-medium transition-colors rounded-lg ${
                        pathname === cat.href
                          ? 'bg-slate-700 text-[#FEDB1A]'
                          : 'text-slate-400 hover:bg-slate-700 hover:text-[#FEDB1A]'
                      }`}
                    >
                      <span>{cat.icon}</span>
                      <span>{cat.label}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Authenticated nav links */}
            {user && authNavLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 font-medium transition-colors rounded-lg ${
                  isActive(link.href)
                    ? 'bg-slate-800 text-[#FEDB1A]'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-[#FEDB1A]'
                }`}
              >
                <span>{link.icon}</span>
                <span>{link.label}</span>
              </Link>
            ))}

            {/* Admin - Expandable */}
            {user && isAdmin && (
              <div>
                <div className="h-px bg-slate-600 my-2" />
                <button
                  onClick={handleAdminClick}
                  className={`w-full flex items-center gap-3 px-4 py-3 font-medium transition-colors rounded-lg ${
                    pathname.startsWith('/admin')
                      ? 'bg-slate-800 text-[#55B1F1]'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-[#55B1F1]'
                  }`}
                >
                  <BsLightning />
                  <span className="flex-1 text-left">Admin</span>
                  <svg
                    className={`w-4 h-4 transition-transform ${isAdminExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isAdminExpanded && (
                  <div className="ml-4 mt-1 space-y-1">
                    {adminCategories.map((cat) => (
                      <Link
                        key={cat.href}
                        href={cat.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex items-center gap-3 px-4 py-2 text-sm font-medium transition-colors rounded-lg ${
                          pathname === cat.href
                            ? 'bg-slate-700 text-[#55B1F1]'
                            : 'text-slate-400 hover:bg-slate-700 hover:text-[#55B1F1]'
                        }`}
                      >
                        <span>{cat.icon}</span>
                        <span>{cat.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="h-px bg-slate-600 my-2" />
            {user ? (
              <>
                <Link
                  href="/profile"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-slate-300 hover:bg-slate-800 hover:text-white rounded-lg"
                >
                  <BsPerson />
                  <span>Profile</span>
                </Link>
                <Link
                  href="/auth/signout"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-slate-300 hover:bg-slate-800 hover:text-white rounded-lg"
                >
                  <BsBoxArrowRight />
                  <span>Sign Out</span>
                </Link>
              </>
            ) : (
              <Link
                href="/auth/signin"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 bg-[#1E4289] text-white hover:bg-[#FEDB1A] hover:text-slate-900 font-medium text-center rounded-lg"
              >
                Sign In
              </Link>
            )}
          </div>
        )}
      </header>
    </>
  )
}

